import "./index.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { useTheme } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./context/I18nContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CartProvider } from "./context/CartContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import BottomNav from "./components/BottomNav";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import { PageLoader } from "./components/ui-bits";
import Landing from "./pages/Landing";

// Everything except the home page loads on demand, so the first visit downloads far less.
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const Invoice = lazy(() => import("./pages/Invoice"));
const Account = lazy(() => import("./pages/Account"));
const Farmers = lazy(() => import("./pages/Farmers"));
const FarmerProfile = lazy(() => import("./pages/FarmerProfile"));
const AITools = lazy(() => import("./pages/AITools"));
const Chat = lazy(() => import("./pages/Chat"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const About = lazy(() => import("./pages/Info").then(m => ({ default: m.About })));
const Contact = lazy(() => import("./pages/Info").then(m => ({ default: m.Contact })));
const Privacy = lazy(() => import("./pages/Info").then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import("./pages/Info").then(m => ({ default: m.Terms })));
const AdminApp = lazy(() => import("./pages/admin/AdminApp"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const NotFound = lazy(() => import("./pages/Info").then(m => ({ default: m.NotFound })));

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return children;
}

const TITLES = { "/": "ULUME - Agri Commerce for India", "/marketplace": "Marketplace", "/cart": "My cart", "/checkout": "Checkout", "/orders": "My orders", "/account": "My account",
  "/wishlist": "My wishlist", "/login": "Sign in", "/forgot-password": "Forgot password", "/reset-password": "Reset password", "/signup": "Create account", "/farmers": "Farms & produce", "/ai-tools": "Farm advisor", "/chat": "Messages", "/dashboard": "Dashboard",
  "/about": "About", "/contact": "Contact us", "/privacy": "Privacy policy", "/terms": "Terms of service" };

function Shell() {
  const { pathname } = useLocation();
  const { theme } = useTheme();
  useEffect(() => {
    const t = TITLES[pathname] || (pathname.startsWith("/orders/") ? "Order details" : pathname.startsWith("/farmers/") ? "Farm profile" : null);
    if (t && !pathname.startsWith("/product/")) document.title = pathname === "/" ? t : `${t} | ULUME`;
  }, [pathname]);
  if (pathname === "/admin/login") return <Suspense fallback={<PageLoader />}><AdminLogin /><Toaster theme={theme} position="top-right" richColors closeButton /></Suspense>;
  if (pathname === "/admin" || pathname.startsWith("/admin/")) return <Suspense fallback={<PageLoader />}><Routes><Route path="/admin/*" element={<AdminApp />} /></Routes><Toaster theme={theme} position="top-right" richColors closeButton /></Suspense>;
  const bare = pathname.endsWith("/invoice");   // printable invoice: no site chrome
  if (bare) return <Suspense fallback={<PageLoader />}><Routes><Route path="/orders/:oid/invoice" element={<Protected><Invoice /></Protected>} /></Routes></Suspense>;
  return (
    <div className="min-h-screen flex flex-col pb-16 md:pb-0">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:m-2 focus:px-3 focus:py-2 focus:rounded-md focus:bg-primary focus:text-primary-foreground">Skip to content</a>
      <Navbar />
      <main id="main" className="flex-1">
        <ErrorBoundary key={pathname}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/product/:pid" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/farmers" element={<Farmers />} />
              <Route path="/farmers/:id" element={<FarmerProfile />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
              <Route path="/ai-tools" element={<Protected><AITools /></Protected>} />
              <Route path="/wishlist" element={<Protected><Wishlist /></Protected>} />
              <Route path="/orders" element={<Protected><Orders /></Protected>} />
              <Route path="/orders/:oid" element={<Protected><OrderDetail /></Protected>} />
              <Route path="/account" element={<Protected><Account /></Protected>} />
              <Route path="/chat" element={<Protected><Chat /></Protected>} />
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
      <BottomNav />
      <Toaster theme={theme} position="top-right" richColors closeButton offset={{ top: 70 }} mobileOffset={{ top: 110 }} />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Shell />
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
